/**
 * ADR-029 (Workforce Composition Reporting) — the shared query-param
 * serialiser for GET /api/employees (and, in Phase 2, the aggregation
 * endpoint, which takes the same filter contract).
 *
 * This pure function is the anti-drift contract between screens (ADR-029
 * D9): every consumer — the Employees Repository, the Workforce
 * Composition report, anything else — must build its query string through
 * it, so identical filter state produces byte-identical query params.
 * Filter BARS are per-screen and not shared; this serialiser is.
 *
 * Conventions it owns:
 * - arrays → comma-joined CSV, empty arrays omitted entirely
 * - sentinels pass through verbatim: 'no-projects' (projectIds),
 *   'no-position' (positionIds)
 * - deliveryCenterIds carries DELIVERY-path semantics (FT project → BU →
 *   cluster); the legacy singular clusterId keeps its employment meaning
 * - booleans only emitted when they differ from the server default
 *   (includeSubUnits default true, includeOverriddenExternal default true)
 * - keys are emitted in a fixed order so serialisations are comparable
 */

const CSV_PARAMS = [
  ['projectIds', 'projectIds'],
  ['positionIds', 'positionIds'],
  ['gradeIds', 'gradeIds'],
  ['contractTypeIds', 'contractTypeIds'],
  ['organizationalStatus', 'organizationalStatus'],
  ['functionalRoleIds', 'functionalRoleIds'],
  ['technicalRoleIds', 'technicalRoleIds'],
  ['organizationalRoleIds', 'organizationalRoleIds'],
  ['deliveryCenterIds', 'deliveryCenterIds'],
  ['serviceUnitIds', 'serviceUnitIds'],
];

const SCALAR_PARAMS = [
  ['search', 'search'],
  ['clusterId', 'clusterId'],
  ['reportingScope', 'reportingScope'],
  ['roleMatch', 'roleMatch'],
  ['roleCompleteness', 'roleCompleteness'],
  ['limit', 'limit'],
  ['cursor', 'cursor'],
  ['orderBy', 'orderBy'],
  ['orderDirection', 'orderDirection'],
];

const toCsv = (value) => {
  const list = Array.isArray(value) ? value : String(value ?? '').split(',');
  return list.map(v => String(v).trim()).filter(Boolean).join(',');
};

/**
 * @param {Object} filterState - see key lists above; arrays or CSV strings
 *   accepted for list params.
 * @returns {URLSearchParams}
 */
export const buildEmployeeQueryParams = (filterState = {}) => {
  const params = new URLSearchParams();

  for (const [key, param] of SCALAR_PARAMS) {
    const value = filterState[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      // roleMatch 'any' is the server default — omit it for stable URLs
      if (key === 'roleMatch' && value === 'any') continue;
      params.append(param, String(value).trim());
    }
  }

  for (const [key, param] of CSV_PARAMS) {
    const csv = toCsv(filterState[key]);
    if (csv !== '') params.append(param, csv);
  }

  if (filterState.managerOnly === true || filterState.managerOnly === 'true') {
    params.append('managerOnly', 'true');
  }
  if (filterState.includeOverriddenExternal === false
      || filterState.includeOverriddenExternal === 'false') {
    params.append('includeOverriddenExternal', 'false');
  }
  if (filterState.includeSubUnits === false || filterState.includeSubUnits === 'false') {
    params.append('includeSubUnits', 'false');
  }

  return params;
};

export default buildEmployeeQueryParams;
